import React, { useState } from 'react';
import { AnyService, ServiceAction } from '../../../types/service.type';
import { useServiceActions } from '../../../hooks/useServiceActions';
import ServiceActionButton from '../ServiceActionButton/ServiceActionButton';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import ServiceActionsMenu from '../ServiceActionMenu/ServiceActionMenu';

interface ServiceCardActionsProps {
  service: AnyService;
  variant: 'compact' | 'default' | 'detailed';
  onActionClick?: (action: string) => void;
  customActions?: React.ReactNode;
}

const ServiceCardActions: React.FC<ServiceCardActionsProps> = ({
  service,
  variant,
  onActionClick,
  customActions,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showStopModal, setShowStopModal] = useState(false);
  
  const {
    isLoading,
    currentAction,
    startService,
    stopService,
    restartService,
    deleteService,
    canVisit,
    visitUrl,
    adminUrl,
  } = useServiceActions({
    service,
    onSuccess: (action) => {
      console.log(`Action ${action} completed successfully`);
      onActionClick?.(action);
    },
    onError: (error) => {
      console.error('Action failed:', error);
    },
  });

  const handleMenuAction = (action: ServiceAction) => {
    switch (action) {
      case 'visit':
        if (visitUrl) {
          window.open(visitUrl, '_blank');
        }
        break;
      case 'admin':
        if (adminUrl) {
          window.open(adminUrl, '_blank');
        }
        break;
      case 'start':
        startService();
        break;
      case 'stop':
        setShowStopModal(true);
        break;
      case 'restart':
        restartService();
        break;
      case 'delete':
        setShowDeleteModal(true);
        break;
      default:
        onActionClick?.(action);
    }
  };

  const handleDelete = async () => {
    await deleteService();
    setShowDeleteModal(false);
  };

  const handleStop = async () => {
    await stopService();
    setShowStopModal(false);
  };

  return (
    <>
      <div className={`
        flex items-center gap-2 mt-4 pt-4 border-t border-gray-100
        ${variant === 'compact' ? 'flex-wrap' : ''}
      `}>
        {/* Custom Actions */}
        {customActions}

        {/* Visit Site Button (Primary) */}
        {!customActions && canVisit && visitUrl && (
          <ServiceActionButton
            action="visit"
            onClick={() => handleMenuAction('visit')}
            variant="primary"
            size={variant === 'compact' ? 'sm' : 'md'}
            className="flex-1"
          />
        )}

        {/* Start Button (when stopped) */}
        {!customActions && service.status === 'stopped' && (
          <ServiceActionButton
            action="start"
            onClick={() => handleMenuAction('start')}
            loading={isLoading && currentAction === 'start'}
            size={variant === 'compact' ? 'sm' : 'md'}
            className="flex-1"
          />
        )}

        {/* Actions Menu */}
        <ServiceActionsMenu
          service={service}
          onActionClick={handleMenuAction}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDelete}
        title="Delete Service"
        message={`Are you sure you want to delete "${service.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        requireInput
        inputPlaceholder="Type the service name to confirm"
        expectedInput={service.name}
        loading={isLoading && currentAction === 'delete'}
      />

      {/* Stop Confirmation Modal */}
      <ConfirmationModal
        isOpen={showStopModal}
        onClose={() => setShowStopModal(false)}
        onConfirm={handleStop}
        title="Stop Service"
        message={`Are you sure you want to stop "${service.name}"?`}
        confirmText="Stop"
        cancelText="Cancel"
        variant="default"
        loading={isLoading && currentAction === 'stop'}
      />
    </>
  );
};

export default ServiceCardActions;